import _defineProperty from '@babel/runtime-corejs3/helpers/esm/defineProperty';
import * as React from 'react';
import styled from 'styled-components';
import classnames from 'classnames';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { jsx } from 'react/jsx-runtime';

let _ = t => t,
  _t;
const scrollableClassName = 'bui-scrollable';

// eslint-disable-next-line @typescript-eslint/naming-convention
const ScrollableArea = /*#__PURE__*/styled.div.withConfig({
  displayName: "ScrollableArea",
  componentId: "sc-11d27kt-0"
})(_t || (_t = _`
  box-sizing: border-box;
  -webkit-overflow-scrolling: touch;
  ${0}
`), function (props) {
  const {
    $horizontalScroll,
    $verticalScroll
  } = props;
  return `overflow-y: ${$verticalScroll ? 'scroll' : 'hidden'};overflow-x: ${$horizontalScroll ? 'scroll' : 'hidden'};`;
});

// eslint-disable-next-line @typescript-eslint/no-type-alias

/**
 * A component that creates a scrollable area and optionally locks the outer scroll.
 * For a component to be scrollable  it needs to have an overflow of content, set dimension restrictions to make this happen.
 * Content overflowing in an axis that is not scrollable will be hidden.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import styled from 'styled-components'
 * import { Scrollable } from '@thisisbud/bui-react-scrollable'
 *
 * const StyledScrollable = styled(Scrollable)`
 *   height: 320px;
 * `
 *
 * function ExampleComponent(props) {
 *   const { children } = props
 *
 *   return (
 *     <StyledScrollable>
 *       {children}
 *     </StyledScrollable>
 *   )
 * }
 * ```
 */
class Scrollable extends React.Component {
  /**
   * The component name displayed in the developer tools.
   */

  /**
   * Default values for optional props.
   */

  constructor(props) {
    super(props);
    _defineProperty(this, "outerScrollLocked", false);
    _defineProperty(this, "contentRef", void 0);
    this.contentRef = /*#__PURE__*/React.createRef();
  }

  /**
   * Activate the outer scroll lock before unmounting the component.
   */
  componentDidMount() {
    this.lockScroll();
  }

  /**
   * Update the outer scroll lock as necessary based on prop changes.
   */
  componentDidUpdate() {
    const {
      lockOuterScroll
    } = this.props;
    if (lockOuterScroll) {
      this.lockScroll();
    } else {
      this.unlockScroll();
    }
  }

  /**
   * Deactivate the outer scroll lock before unmounting the component.
   */
  componentWillUnmount() {
    this.unlockScroll();
  }

  /**
   * Lock the outer scroll if unlocked and enabled.
   */
  lockScroll() {
    const {
      lockOuterScroll
    } = this.props;
    if (lockOuterScroll && !this.outerScrollLocked) {
      const contentElement = this.contentRef.current;
      disableBodyScroll(contentElement, {
        reserveScrollBarGap: true
      });
      this.outerScrollLocked = true;
    }
  }

  /**
   * Unlock the outer scroll if locked.
   */
  unlockScroll() {
    const contentElement = this.contentRef.current;
    if (this.outerScrollLocked) {
      enableBodyScroll(contentElement);
      this.outerScrollLocked = false;
    }
  }

  /**
   * Current state of the outer scroll lock.
   */

  /**
   * Render the component.
   */
  render() {
    const {
      children,
      className,
      horizontalScroll,
      verticalScroll
    } = this.props;
    return /*#__PURE__*/jsx(ScrollableArea, {
      $horizontalScroll: horizontalScroll,
      $verticalScroll: verticalScroll,
      className: classnames(scrollableClassName, className),
      ref: this.contentRef,
      tabIndex: 0,
      children: children
    });
  }
}
_defineProperty(Scrollable, "displayName", 'Scrollable');
_defineProperty(Scrollable, "defaultProps", {
  horizontalScroll: false,
  lockOuterScroll: false,
  verticalScroll: true
});

export { Scrollable, scrollableClassName };
